import React, { useEffect, useMemo, useState } from 'react';
import useExtraSmallScreen from 'hooks/useExtraSmallScreen';
import useSmallScreen from 'hooks/useSmallScreen';
import AppBar from 'housecall-ui/dist/DesignSystem/Navigation/TopNavigation/AppBar/AppBar';
import Toolbar from 'housecall-ui/dist/DesignSystem/Navigation/TopNavigation/Toolbar';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { useBillingEnabledFeatures } from 'resources/Organization/Features';

import NavbarSpacer from 'components/Common/Navigation/NavbarSpacer';
import useNavbarHeight from 'components/Common/Navigation/useNavbarHeight';
import { useMyMoneyNotificationsCount } from 'components/MyMoneyV2/context/MyMoneyNotificationsCountContext';
import url from 'components/url';

import DesktopNav from './components/DesktopNav';
import MobileNav from './components/MobileNav';
import TabletNav from './components/TabletNav';
import { useShouldDisplayCustomers, useShouldDisplayMyMoney } from './displayConfig';
import { NavProps, NavTab } from './types';

const useScreenSize = () => {
  const isMobile = useExtraSmallScreen();
  const isTablet = useSmallScreen();

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';

  return 'desktop';
};

const useSyncActiveTabWithPathOnFirstRender = (
  tabs: { label: string; path: string }[],
  onChange: (args: number | false) => void
) => {
  useEffect(() => {
    const syncActiveTabWithPath = () => {
      const indexOfMatchedTab = tabs.findIndex((tab: NavTab) => {
        return window.location.pathname.includes(tab.path);
      });

      return onChange(indexOfMatchedTab === -1 ? false : indexOfMatchedTab);
    };

    syncActiveTabWithPath();
  }, [tabs, onChange]);
};

const useStyles = makeStyles((theme) => ({
  toolbarHeight: {
    height: theme.spacing(9), // matches HC Web toolbar height
  },
}));

type PLGNavbarProps = {
  banner?: React.ReactNode;
};

export default function PLGNavbar({ banner }: PLGNavbarProps) {
  const [activeTab, setActiveTab] = useState<number | false>(0);

  const classes = useStyles();
  const screenSize = useScreenSize();

  const { setNavbarHeight } = useNavbarHeight();
  const { isLoading: isFeaturesLoading } = useBillingEnabledFeatures();
  const myMoneyNotificationsCount = useMyMoneyNotificationsCount();

  const shouldDisplayCustomersTabs = useShouldDisplayCustomers();
  const shouldDisplayPaymentTabs = useShouldDisplayMyMoney();

  const tabs = useMemo(() => {
    const defaultTabs = [{ label: 'Home', path: url.dashboard.path }];
    const customerTabs = [{ label: 'Customers', path: url.customers.path }];
    const paymentTabs = [{ label: 'My money', path: url.myMoney.path, badgeValue: myMoneyNotificationsCount }];

    return [
      ...defaultTabs,
      ...(shouldDisplayCustomersTabs ? customerTabs : []),
      ...(shouldDisplayPaymentTabs ? paymentTabs : []),
    ];
  }, [shouldDisplayCustomersTabs, shouldDisplayPaymentTabs, myMoneyNotificationsCount]);

  useSyncActiveTabWithPathOnFirstRender(tabs, setActiveTab);

  const handleAppBarRef = (el: HTMLElement) => {
    if (!el) return;
    setNavbarHeight(el.clientHeight);
  };

  const handleTabChange = (_: React.ChangeEvent<unknown>, tabIndex: number | false) => {
    setActiveTab(tabIndex);
  };

  const navProps: NavProps = {
    onTabChange: handleTabChange,
    activeTab,
    tabs,
  };

  const activeNav = {
    mobile: <MobileNav {...navProps} />,
    tablet: <TabletNav {...navProps} />,
    desktop: <DesktopNav {...navProps} />,
  };

  return (
    <HousecallThemeProvider mode="dark">
      <AppBar version="v2" id="hc-toolbar" ref={handleAppBarRef}>
        {banner}
        <Toolbar version="v2" component="nav" className={classes.toolbarHeight}>
          {isFeaturesLoading ? null : activeNav[screenSize]}
        </Toolbar>
      </AppBar>
      <NavbarSpacer />
    </HousecallThemeProvider>
  );
}
